/** Same statuses the inventory map removes before it shows seats still for sale. */
const UNAVAILABLE_SALE_STATUSES = new Set([
  'sold',
  'reserved',
  'reservedForGroupPurchase',
  'pendingTransfer',
  'resaleAvailable',
])

export function unavailableSeatIds(tickets) {
  const ids = new Set()
  for (const ticket of tickets || []) {
    if (
      UNAVAILABLE_SALE_STATUSES.has(ticket?.on_sale_status) &&
      ticket.seatId != null
    ) {
      ids.add(ticket.seatId)
    }
  }
  return ids
}

function seatIdsOn(inventory) {
  if (!inventory) return []
  const seats = Array.isArray(inventory.seats) ? inventory.seats : []
  const gaLists = inventory.gaSeats ? Object.values(inventory.gaSeats) : []
  const gaSeats = gaLists.flatMap((list) => (Array.isArray(list) ? list : []))
  return [...seats, ...gaSeats]
}

/** Seats still assigned to this inventory after sold and reserved tickets are removed. */
export function availableSeatCount(inventory, unavailableIds, heldOrKilledIds) {
  const blocked = new Set(unavailableIds || [])
  for (const id of heldOrKilledIds || []) blocked.add(id)
  return seatIdsOn(inventory).filter((id) => !blocked.has(id)).length
}

/**
 * Spot ids an unmatched GA sale takes. A sale whose ticket already carries an
 * open spot id does not take a second one.
 */
function unmatchedOpenGaSpotIds(inventory, tickets, heldOrKilledIds) {
  const ids = new Set()
  const blocked =
    heldOrKilledIds instanceof Set
      ? heldOrKilledIds
      : new Set(heldOrKilledIds || [])
  const claimed = new Set()
  const consumed = new Set()
  for (const [sectionId, spots] of Object.entries(inventory?.gaSeats || {})) {
    if (!Array.isArray(spots)) continue
    const spotSet = new Set(spots)
    const unmatched = (tickets || []).filter((ticket) => {
      if (claimed.has(ticket)) return false
      if (!UNAVAILABLE_SALE_STATUSES.has(ticket?.on_sale_status)) return false
      if (String(ticket?.sectionId || '') !== String(sectionId)) return false
      if (
        ticket.seatId != null &&
        (spotSet.has(ticket.seatId) || blocked.has(ticket.seatId))
      ) {
        return false
      }
      return true
    })
    let index = 0
    for (const spotId of spots) {
      if (index >= unmatched.length) break
      const alreadySold = (tickets || []).some(
        (ticket) =>
          ticket?.seatId === spotId &&
          UNAVAILABLE_SALE_STATUSES.has(ticket?.on_sale_status)
      )
      if (blocked.has(spotId) || alreadySold || consumed.has(spotId)) continue
      claimed.add(unmatched[index])
      index += 1
      consumed.add(spotId)
      ids.add(spotId)
    }
  }
  return ids
}

export function remainingSeatCount(
  inventory,
  tickets,
  heldOrKilledIds,
  options = {}
) {
  const unavailableIds = unavailableSeatIds(tickets)
  if (options.consumeUnmatchedGa) {
    unmatchedOpenGaSpotIds(inventory, tickets, heldOrKilledIds).forEach((id) =>
      unavailableIds.add(id)
    )
  }
  const seatCount = availableSeatCount(
    inventory,
    unavailableIds,
    heldOrKilledIds
  )
  return seatCount
}

/** Seat ids assigned to a hold or kill, including categories and GA seats. */
export function heldOrKilledSeatIds(holds, kills) {
  const ids = seatIdsInMap(holds)
  for (const id of seatIdsInMap(kills)) ids.add(id)
  return ids
}

export function inventoryForOffer(offer, openInventory) {
  if (offer?.inventoryType?.toLowerCase() === 'open') return openInventory
  return offer
}

function seatCountIn(node, seen = new Set()) {
  if (!node || typeof node !== 'object' || seen.has(node)) return 0
  seen.add(node)

  let count = Array.isArray(node.seats) ? node.seats.length : 0
  if (node.gaSeats && typeof node.gaSeats === 'object') {
    for (const list of Object.values(node.gaSeats)) {
      if (Array.isArray(list)) count += list.length
    }
  }
  if (node.categories && typeof node.categories === 'object') {
    for (const category of Object.values(node.categories)) {
      count += seatCountIn(category, seen)
    }
  }
  return count
}

function seatCountInMap(map) {
  if (!map || typeof map !== 'object') return 0
  return Object.values(map).reduce(
    (total, item) => total + seatCountIn(item),
    0
  )
}

function seatIdsIn(node, ids = new Set(), seen = new Set()) {
  if (!node || typeof node !== 'object' || seen.has(node)) return ids
  seen.add(node)
  for (const id of Array.isArray(node.seats) ? node.seats : []) ids.add(id)
  if (node.gaSeats && typeof node.gaSeats === 'object') {
    for (const list of Object.values(node.gaSeats)) {
      if (Array.isArray(list)) list.forEach((id) => ids.add(id))
    }
  }
  if (node.categories && typeof node.categories === 'object') {
    for (const category of Object.values(node.categories)) {
      seatIdsIn(category, ids, seen)
    }
  }
  return ids
}

function seatIdsInMap(map) {
  const ids = new Set()
  if (!map || typeof map !== 'object') return ids
  for (const item of Object.values(map)) seatIdsIn(item, ids)
  return ids
}

/**
 * Seats still assigned to this inventory after sold, held, and killed seats
 * are removed. Null when nothing was assigned. 0 when every assigned seat
 * is sold, held, or killed. Open uses the shared pool. Exclusive uses only
 * seats and tickets that belong to this offer.
 */
export function assignedSeatCount(
  inventory,
  { holds, kills, tickets, offer, openInventory } = {}
) {
  const source = { ...(offer || {}), ...(inventory || {}) }
  const inventoryType =
    source.type || source.inventoryType || offer?.inventoryType
  const resolved = { ...source, inventoryType }
  const isOpen = String(inventoryType || '').toLowerCase() === 'open'
  const context = {
    openInventory: isOpen ? openInventory || inventory : openInventory,
    holds,
    kills,
    tickets,
  }

  // Same gate as "No inventory created": nothing assigned to this offer.
  if (!offerHasCreatedInventory(resolved, context)) return null

  return remainingSeatCount(
    isOpen ? context.openInventory || inventory : inventory,
    tickets,
    isOpen ? heldOrKilledSeatIds(holds, kills) : undefined
  )
}

function ticketMatchesOffer(ticket, offer) {
  const offerId = offer?.id != null ? String(offer.id) : null
  const offerUUID = offer?.uuid != null ? String(offer.uuid) : null
  const ticketOfferId = ticket?.offer?.id ?? ticket?.offer
  return (
    (offerId && ticketOfferId != null && String(ticketOfferId) === offerId) ||
    (offerUUID &&
      ticket?.offerUUID != null &&
      String(ticket.offerUUID) === offerUUID)
  )
}

/**
 * Sold out only after seats were assigned and none are left to sell.
 * A new offer with an empty inventory list stays on sale.
 * Open offers are sold out when nothing is left to sell, including seats on a
 * hold or kill. Exclusive offers are sold out only when their seats are sold.
 */
export function isOfferSoldOut(
  offer,
  { openInventory, holds, kills, tickets, saleStarted = false } = {}
) {
  const hasInventory = offerHasCreatedInventory(offer, {
    openInventory,
    holds,
    kills,
    tickets,
  })
  if (!hasInventory || !saleStarted) return false
  const isOpen = offer?.inventoryType?.toLowerCase() === 'open'
  const seatsLeft = remainingSeatCount(
    inventoryForOffer(offer, openInventory),
    tickets,
    isOpen ? heldOrKilledSeatIds(holds, kills) : undefined
  )
  // The stored flag can still say sold out after seats were put back.
  // The count on the offers row uses the seats that are left.
  return seatsLeft === 0
}

/**
 * True once seats have been assigned to this offer. Open offers share the
 * event pool, including seats on a hold or kill. Exclusive offers count only
 * seats that are still theirs. Seats moved to a hold or kill do not count.
 * Sold seats still count. An empty list with nothing sold for that offer is
 * false.
 */
export function offerHasCreatedInventory(
  offer,
  { openInventory, holds, kills, tickets } = {}
) {
  const isOpen = offer?.inventoryType?.toLowerCase() === 'open'
  const inventory = inventoryForOffer(offer, openInventory)

  if (isOpen) {
    if (seatCountIn(inventory) > 0) return true
    if (seatCountInMap(holds) > 0) return true
    if (seatCountInMap(kills) > 0) return true
    if (unavailableSeatIds(tickets).size > 0) return true
    return false
  }

  const blocked = heldOrKilledSeatIds(holds, kills)
  const ownSeats = seatIdsOn(inventory).filter((id) => !blocked.has(id))
  if (ownSeats.length > 0) return true

  return (tickets || []).some(
    (ticket) =>
      UNAVAILABLE_SALE_STATUSES.has(ticket?.on_sale_status) &&
      ticketMatchesOffer(ticket, offer)
  )
}

/**
 * Seat list for the event's general on-sale offer. Inventory is keyed by
 * offer id, and the first entry can be an exclusive offer.
 */
export function openInventoryForEvent(event) {
  const inventoryOffers = event?.inventory?.offers || {}
  const generalOnsale = (event?.offers || []).find(
    (offer) => offer?.isDefaultOffer
  )
  if (generalOnsale?.id != null) {
    const key = String(generalOnsale.id)
    if (inventoryOffers[key]) return inventoryOffers[key]
    const match = Object.values(inventoryOffers).find(
      (entry) => entry && String(entry.id) === key
    )
    if (match) return match
  }
  return Object.values(inventoryOffers).find((entry) => {
    if (!entry || typeof entry !== 'object') return false
    if (String(entry.inventoryType || '').toLowerCase() === 'exclusive') {
      return false
    }
    const inventoryType = String(entry.inventoryType || '').toLowerCase()
    const name = String(entry.name || '').toLowerCase()
    return (
      inventoryType === 'open' ||
      name === 'open' ||
      name === 'standard admission' ||
      entry.isDefaultOffer === true
    )
  })
}

/**
 * Period to show after the scheduler clears `active` on a closed promo.
 * Prefers the ended custom window over a copied general on-sale window that
 * is still in the future, so the offers row stays "Ended" instead of blank.
 */
export function closedAvailabilityPeriod(periods, now = new Date()) {
  const nowTime = now instanceof Date ? now.getTime() : new Date(now).getTime()
  if (Number.isNaN(nowTime)) return

  const ended = (periods || []).filter((period) => {
    if (!period?.ends) return false
    const ends = new Date(period.ends).getTime()
    return !Number.isNaN(ends) && ends < nowTime
  })
  if (!ended.length) return

  const custom = ended.filter(
    (period) => !String(period.name || '').includes('General On-sale')
  )
  const pool = custom.length ? custom : ended
  return pool.reduce((best, period) => {
    if (!best) return period
    const periodStart = new Date(period.starts || 0).getTime()
    const bestStart = new Date(best.starts || 0).getTime()
    return periodStart >= bestStart ? period : best
  }, null)
}

/**
 * True when every main offer that has inventory is sold out or hidden.
 * An exclusive offer with no seats of its own does not keep the event on sale.
 * An event whose offers have no inventory stays on sale.
 */
export function isEventSoldOut(offers, context = {}) {
  const withInventory = (offers || []).filter(
    (offer) =>
      !offer?.isConnectedOffer && offerHasCreatedInventory(offer, context)
  )
  if (!withInventory.length) return false
  return withInventory.every((offer) => {
    if (offer?.isHidden) return true
    const saleStarted =
      typeof context.saleStarted === 'function'
        ? context.saleStarted(offer)
        : context.saleStarted
    return isOfferSoldOut(offer, { ...context, saleStarted })
  })
}
